import React, { useMemo, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Dialog, DialogContent, IconButton, Tooltip, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Funnel,
  FunnelChart,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Treemap,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { downloadCsv } from "./downloadUtils";
import { ASSISTANT_EXPANDED_DIALOG_Z_INDEX } from "./constants";

const PALETTE = ["#2563eb", "#16a34a", "#ea580c", "#9333ea", "#dc2626", "#0891b2", "#ca8a04"];

function pivotLongFormatData({ data, xKey, seriesKey, valueKey }) {
  const pivoted = new Map();

  data.forEach((row) => {
    const xVal = row[xKey];
    const seriesVal = String(row[seriesKey]);
    const existing = pivoted.get(xVal) ?? { [xKey]: xVal };
    existing[seriesVal] = row[valueKey];
    pivoted.set(xVal, existing);
  });

  return {
    plotData: Array.from(pivoted.values()),
    seriesNames: Array.from(new Set(data.map((row) => String(row[seriesKey])))),
  };
}

function ChartWrap({
  title,
  children,
  height = 260,
  disableResponsive = false,
  downloadColumns = [],
  downloadRows = [],
  downloadName = "chart-data.csv",
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDownload = () => {
    downloadCsv({
      columns: downloadColumns,
      rows: downloadRows,
      filename: downloadName,
    });
  };

  return (
    <>
      <MDBox
        mt={1}
        sx={{
          borderRadius: "12px",
          border: "1px solid rgba(59, 130, 246, 0.18)",
          bgcolor: "background.paper",
          p: 1.5,
        }}
      >
        <MDBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{title || "Chart"}</Typography>
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <Tooltip title="Download CSV">
              <IconButton size="small" onClick={handleDownload} aria-label="download chart data">
                <DownloadIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Expand">
              <IconButton size="small" onClick={() => setIsExpanded(true)} aria-label="expand chart">
                <OpenInFullIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </MDBox>
        </MDBox>
        <MDBox sx={{ width: "100%", height }}>
          {disableResponsive ? children : <ResponsiveContainer>{children}</ResponsiveContainer>}
        </MDBox>
      </MDBox>
      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        maxWidth="xl"
        fullWidth
        sx={{ zIndex: ASSISTANT_EXPANDED_DIALOG_Z_INDEX }}
      >
        <DialogContent sx={{ p: 1.5 }}>
          <MDBox sx={{ width: "100%", height: Math.max(height * 2, 460) }}>
            {disableResponsive ? children : <ResponsiveContainer>{children}</ResponsiveContainer>}
          </MDBox>
        </DialogContent>
      </Dialog>
    </>
  );
}

function renderAnnotations(annotations, chartType) {
  if (!Array.isArray(annotations) || annotations.length === 0) {
    return null;
  }

  const supportsAnnotations = new Set([
    "line",
    "bar",
    "stacked_bar",
    "area",
    "stacked_area",
    "horizontal_bar",
    "combo",
    "histogram",
  ]);

  if (!supportsAnnotations.has(chartType)) {
    return null;
  }

  return annotations.map((annotation, index) => {
    if (annotation.type === "target_line" || annotation.type === "threshold_line") {
      return (
        <ReferenceLine
          key={`annotation-line-${index}`}
          {...(annotation.axis === "x" ? { x: annotation.value } : { y: annotation.value })}
          stroke={annotation.color ?? "#ef4444"}
          strokeDasharray="4 4"
          label={annotation.label}
        />
      );
    }

    if (annotation.type === "band") {
      return (
        <ReferenceArea
          key={`annotation-band-${index}`}
          {...(annotation.axis === "x"
            ? { x1: annotation.value, x2: annotation.value_end }
            : { y1: annotation.value, y2: annotation.value_end })}
          fill={annotation.color ?? "#fef3c7"}
          fillOpacity={0.4}
          label={annotation.label}
        />
      );
    }

    return null;
  });
}

export default function ChartRenderer({
  chart_type: chartType,
  data = [],
  x_key: xKey,
  y_keys: yKeys,
  series_key: seriesKey,
  value_key: valueKey,
  title,
  annotations,
  row_key: rowKey = "row",
  col_key: colKey = "col",
  gauge_config: gaugeConfig,
}) {
  if (!chartType || !data.length) {
    if (chartType !== "gauge") {
      return null;
    }
  }

  const chartTypesWithoutXKey = new Set(["gauge", "heatmap", "treemap"]);
  if (!xKey && !chartTypesWithoutXKey.has(chartType)) {
    return null;
  }

  let plotData = data;
  let seriesNames = yKeys?.length ? yKeys : ["value"];

  if (seriesKey && valueKey) {
    const pivoted = pivotLongFormatData({ data, xKey, seriesKey, valueKey });
    plotData = pivoted.plotData;
    seriesNames = pivoted.seriesNames;
  }

  const downloadColumns = useMemo(() => {
    const sampleRow = plotData[0] ?? {};
    return Object.keys(sampleRow).map((key) => ({ key, label: key }));
  }, [plotData]);

  const chartWrapProps = {
    title,
    downloadColumns,
    downloadRows: plotData,
    downloadName: `${(title || chartType || "chart").toLowerCase().replace(/\s+/g, "-")}.csv`,
  };

  if (chartType === "line") {
    return (
      <ChartWrap {...chartWrapProps}>
        <LineChart data={plotData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          {renderAnnotations(annotations, chartType)}
          {seriesNames.map((series, index) => (
            <Line key={series} dataKey={series} stroke={PALETTE[index % PALETTE.length]} dot={false} />
          ))}
        </LineChart>
      </ChartWrap>
    );
  }

  if (chartType === "bar" || chartType === "stacked_bar") {
    return (
      <ChartWrap {...chartWrapProps}>
        <BarChart data={plotData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          {renderAnnotations(annotations, chartType)}
          {seriesNames.map((series, index) => (
            <Bar
              key={series}
              dataKey={series}
              fill={PALETTE[index % PALETTE.length]}
              stackId={chartType === "stacked_bar" ? "a" : undefined}
            />
          ))}
        </BarChart>
      </ChartWrap>
    );
  }

  if (chartType === "area" || chartType === "stacked_area") {
    return (
      <ChartWrap {...chartWrapProps}>
        <AreaChart data={plotData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          {renderAnnotations(annotations, chartType)}
          {seriesNames.map((series, index) => (
            <Area
              key={series}
              dataKey={series}
              stroke={PALETTE[index % PALETTE.length]}
              fill={PALETTE[index % PALETTE.length]}
              fillOpacity={0.3}
              stackId={chartType === "stacked_area" ? "a" : undefined}
            />
          ))}
        </AreaChart>
      </ChartWrap>
    );
  }

  if (chartType === "pie") {
    return (
      <ChartWrap {...chartWrapProps}>
        <PieChart>
          <RechartsTooltip />
          <Legend />
          <Pie data={plotData} dataKey={seriesNames[0] ?? "value"} nameKey={xKey} outerRadius={100}>
            {plotData.map((_, index) => (
              <Cell key={index} fill={PALETTE[index % PALETTE.length]} />
            ))}
          </Pie>
        </PieChart>
      </ChartWrap>
    );
  }

  if (chartType === "scatter") {
    return (
      <ChartWrap {...chartWrapProps}>
        <ScatterChart>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis dataKey={seriesNames[0]} />
          <RechartsTooltip />
          <Legend />
          <Scatter data={plotData} fill={PALETTE[0]} />
        </ScatterChart>
      </ChartWrap>
    );
  }

  if (chartType === "horizontal_bar") {
    return (
      <ChartWrap {...chartWrapProps}>
        <BarChart data={plotData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis type="category" dataKey={xKey} width={140} />
          <RechartsTooltip />
          <Legend />
          {renderAnnotations(annotations, chartType)}
          {seriesNames.map((series, index) => (
            <Bar key={series} dataKey={series} fill={PALETTE[index % PALETTE.length]} />
          ))}
        </BarChart>
      </ChartWrap>
    );
  }

  if (chartType === "histogram") {
    const binKey = xKey || "bin";
    const countKey = valueKey || seriesNames[0] || "count";
    return (
      <ChartWrap {...chartWrapProps}>
        <BarChart data={plotData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={binKey} />
          <YAxis />
          <RechartsTooltip />
          {renderAnnotations(annotations, chartType)}
          <Bar dataKey={countKey} fill={PALETTE[0]} />
        </BarChart>
      </ChartWrap>
    );
  }

  if (chartType === "combo") {
    const [barKey, lineKey] = seriesNames;
    return (
      <ChartWrap {...chartWrapProps}>
        <ComposedChart data={plotData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" />
          <RechartsTooltip />
          <Legend />
          {renderAnnotations(annotations, chartType)}
          {barKey ? <Bar yAxisId="left" dataKey={barKey} fill={PALETTE[0]} /> : null}
          {lineKey ? <Line yAxisId="right" dataKey={lineKey} stroke={PALETTE[2]} dot={false} /> : null}
        </ComposedChart>
      </ChartWrap>
    );
  }

  if (chartType === "treemap") {
    return (
      <ChartWrap {...chartWrapProps}>
        <Treemap data={plotData} dataKey={valueKey ?? "value"} stroke="#fff" fill={PALETTE[0]} />
      </ChartWrap>
    );
  }

  if (chartType === "funnel") {
    return (
      <ChartWrap {...chartWrapProps}>
        <FunnelChart>
          <RechartsTooltip />
          <Funnel dataKey={valueKey ?? "value"} data={plotData} isAnimationActive>
            <LabelList position="right" fill="#1e293b" dataKey={xKey} />
          </Funnel>
        </FunnelChart>
      </ChartWrap>
    );
  }

  if (chartType === "radar") {
    return (
      <ChartWrap {...chartWrapProps}>
        <RadarChart data={plotData}>
          <PolarGrid />
          <PolarAngleAxis dataKey={xKey} />
          <PolarRadiusAxis />
          <RechartsTooltip />
          <Legend />
          {seriesNames.map((series, index) => (
            <Radar
              key={series}
              dataKey={series}
              stroke={PALETTE[index % PALETTE.length]}
              fill={PALETTE[index % PALETTE.length]}
              fillOpacity={0.3}
            />
          ))}
        </RadarChart>
      </ChartWrap>
    );
  }

  if (chartType === "heatmap") {
    const rowValues = Array.from(new Set(plotData.map((row) => row[rowKey])));
    const colValues = Array.from(new Set(plotData.map((row) => row[colKey])));
    const heatValueKey = valueKey || "value";
    const lookup = new Map(
      plotData.map((row) => [`${String(row[rowKey])}::${String(row[colKey])}`, Number(row[heatValueKey]) || 0])
    );
    const maxValue = Math.max(...plotData.map((row) => Number(row[heatValueKey]) || 0), 0);

    return (
      <ChartWrap {...chartWrapProps} disableResponsive>
        <MDBox
          sx={{
            display: "grid",
            gap: "1px",
            bgcolor: "#e2e8f0",
            p: "1px",
            gridTemplateColumns: `auto repeat(${colValues.length}, minmax(60px, 1fr))`,
          }}
        >
          <MDBox sx={{ bgcolor: "white" }} />
          {colValues.map((col) => (
            <MDBox
              key={`col-${String(col)}`}
              sx={{ bgcolor: "white", px: 1, py: 0.5, fontSize: "0.7rem", textAlign: "center", fontWeight: 600 }}
            >
              {String(col)}
            </MDBox>
          ))}
          {rowValues.map((row) => (
            <React.Fragment key={`row-group-${String(row)}`}>
              <MDBox sx={{ bgcolor: "white", px: 1, py: 0.5, fontSize: "0.7rem", fontWeight: 600 }}>
                {String(row)}
              </MDBox>
              {colValues.map((col) => {
                const value = lookup.get(`${String(row)}::${String(col)}`) ?? 0;
                const intensity = maxValue ? value / maxValue : 0;
                return (
                  <MDBox
                    key={`cell-${String(row)}-${String(col)}`}
                    sx={{
                      px: 1,
                      py: 0.5,
                      fontSize: "0.7rem",
                      textAlign: "center",
                      bgcolor: `rgba(37, 99, 235, ${0.1 + intensity * 0.7})`,
                      color: intensity > 0.5 ? "white" : "inherit",
                    }}
                  >
                    {Number.isFinite(value) ? value.toLocaleString() : String(value)}
                  </MDBox>
                );
              })}
            </React.Fragment>
          ))}
        </MDBox>
      </ChartWrap>
    );
  }

  if (chartType === "gauge") {
    const value = Number(gaugeConfig?.value ?? plotData[0]?.value ?? 0);
    const min = Number(gaugeConfig?.min ?? 0);
    const max = Number(gaugeConfig?.max ?? 100);
    const target = gaugeConfig?.target;
    const pct = Math.max(0, Math.min(1, (value - min) / Math.max(1, max - min)));
    const ringData = [{ name: "value", value: pct }, { name: "rest", value: 1 - pct }];

    return (
      <ChartWrap {...chartWrapProps} disableResponsive height={220}>
        <MDBox sx={{ width: "100%", height: 170 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={ringData} dataKey="value" startAngle={180} endAngle={0} innerRadius={70} outerRadius={100}>
                <Cell fill={PALETTE[1]} />
                <Cell fill="#e5e7eb" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </MDBox>
        <Typography
          sx={{
            mt: "-36px",
            textAlign: "center",
            fontSize: "1.4rem",
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          {value}
        </Typography>
        {target !== undefined ? (
          <Typography sx={{ textAlign: "center", fontSize: "0.7rem", color: "text.secondary" }}>
            target {target}
          </Typography>
        ) : null}
      </ChartWrap>
    );
  }

  return null;
}
